import { showErrorMessage } from '../../../../components/message-boxes/ErrorMessage';
import {
  getCanonicalTypeInfo,
  getColumnInfo,
  performUserSelectionAction,
  removeSelectionStep,
} from '../../../../data/analyticsDataSlice';
import { isMultiValuedType } from '../../../../data/interfaces';
import { TpEnSelectionFilterTypes } from '../../../../data/selection/interfaces';
import { parseEnSelectionFilter } from '../../../../data/selection/selectionEnFilters';
import { getDataTable } from '../../charts/chartDataBindings';
import { convertColumnDataListToDisplayString, hasSelectedItems, isSelectedItem } from '../../helpers/misc';
import { TpDashboardActionContext, TpDbActionStepDefinition as TpDbActionStepTypeDefinition } from '../interfaces';
import { generateFilterSchema, getFilteredRowNrs } from './dbActionStepTypeTableJoin';

function getJsonSchema(): any {
  return {
    type: 'object',
    title: 'Transfer Selection',
    description:
      'Transfer the current selection from a source table to a destination table, matching a common identifier column.',
    properties: {
      type: { type: 'string', const: 'TransferSelection' },
      sourceTableId: {
        type: 'string',
        description: 'Identifier of the source table, where the selection is transferred from.',
      },
      destTableId: {
        type: 'string',
        description: 'Identifier of the destination table, where the selection is transferred to.',
      },
      sourceMatchingColId: {
        type: 'string',
        description: 'Identifier of the column in the source table, used for matching.',
      },
      destMatchingColId: {
        type: 'string',
        description: 'Identifier of the column in the destination table, used for matching.',
      },
      stepIdentifier: {
        type: 'string',
        description:
          'Identifier for the type of filtering step. If this type of step is already present in the filter, that step will be overwritten.',
      },
      allowEmptySelection: {
        type: 'boolean',
        description: 'If not set, an error is displayed in case the source does not have a selection (default: false).',
      },
      sourceFilter: generateFilterSchema(
        'Optional filter definition. The transferred selection in the source table will be restricted to this filter.',
      ),
    },
    required: ['type', 'sourceTableId', 'destTableId', 'sourceMatchingColId', 'destMatchingColId', 'stepIdentifier'],
  };
}

function execute(ctx: TpDashboardActionContext, step: any, onCompleted: () => void) {
  const sourceTableId = step.sourceTableId as string;
  const destTableId = step.destTableId as string;

  const sourceMatchingColId = step.sourceMatchingColId as string;
  const destMatchingColId = step.destMatchingColId as string;
  const sourceTable = getDataTable(ctx.dataContext, sourceTableId);
  const destTable = getDataTable(ctx.dataContext, destTableId);

  const sourceCanonicalTypeInfo = getCanonicalTypeInfo(ctx.dataContext, sourceTable.canonicalTypeId);
  const sourceSelectedItemsMap = sourceCanonicalTypeInfo.selectedItemsMap;

  if (!step.allowEmptySelection) {
    if (!hasSelectedItems(sourceSelectedItemsMap)) {
      showErrorMessage(`Please select some ${sourceCanonicalTypeInfo.name} items before using this action.`);
      return;
    }
  }

  if (!hasSelectedItems(sourceSelectedItemsMap)) {
    // we need a special approach in case the source does not have any selection anymore: simply remove that selection step from the destination
    ctx.dispatch(
      removeSelectionStep({
        dataContextId: ctx.dataContext.id,
        canonicalTypeId: destTable.canonicalTypeId,
        key: step.stepIdentifier,
      }),
    );
    onCompleted();
    return;
  }

  const sourceDataItemId = sourceTable.columnData[sourceTable.colItemId];
  const sourceMatchingCol = getColumnInfo(sourceTable, sourceMatchingColId);
  const destMatchingCol = getColumnInfo(destTable, destMatchingColId);
  const destDataItemId = destTable.columnData[destTable.colItemId];
  const matchingDestItemIds = [];

  const sourceMatchingColumnData = convertColumnDataListToDisplayString(
    sourceMatchingCol,
    sourceTable.columnData[sourceMatchingColId],
  );

  const destMatchingColumnData = convertColumnDataListToDisplayString(
    destMatchingCol,
    destTable.columnData[destMatchingColId],
  );

  let sourceFilter = null;
  if (step.sourceFilter) sourceFilter = parseEnSelectionFilter(ctx.dataContext, sourceTableId, step.sourceFilter);

  const sourceRowNrs = getFilteredRowNrs(ctx, sourceTable, step.sourceFilter);
  const selectedSourceMatchingVals: { [id: string]: boolean } = {};

  for (const sourceRowNr of sourceRowNrs) {
    if (isSelectedItem(sourceSelectedItemsMap, sourceDataItemId[sourceRowNr])) {
      const sourceMatchingVal = sourceMatchingColumnData[sourceRowNr];
      if (sourceMatchingVal && sourceMatchingVal.trim() !== '') {
        selectedSourceMatchingVals[sourceMatchingVal] = true;
      }
    }
  }
  const selectedSourceMatchingValueSet = new Set(Object.keys(selectedSourceMatchingVals));
  // we need a different approach if one of the columns is multi-valued
  if (isMultiValuedType(sourceMatchingCol.valueType)) {
    const allUniqueSourceMatching = new Set();
    Object.keys(selectedSourceMatchingVals).forEach((val) => {
      val.split(';').forEach((v) => allUniqueSourceMatching.add(v.trim()));
    });
    for (let destRowNr = 0; destRowNr < destDataItemId.length; destRowNr++) {
      if (allUniqueSourceMatching.has(destMatchingColumnData[destRowNr]))
        matchingDestItemIds.push(destDataItemId[destRowNr]);
    }
  } else if (isMultiValuedType(destMatchingCol.valueType)) {
    for (let destRowNr = 0; destRowNr < destDataItemId.length; destRowNr++) {
      const destMatchingValues = destMatchingColumnData[destRowNr].split(';');
      for (const destMatchingValue of destMatchingValues) {
        if (selectedSourceMatchingValueSet.has(destMatchingValue.trim())) {
          matchingDestItemIds.push(destDataItemId[destRowNr]);
          break;
        }
      }
    }
  } else {
    for (let destRowNr = 0; destRowNr < destDataItemId.length; destRowNr++) {
      if (selectedSourceMatchingVals[destMatchingColumnData[destRowNr]])
        matchingDestItemIds.push(destDataItemId[destRowNr]);
    }
  }
  ctx.dispatch(
    performUserSelectionAction({
      dataContextId: ctx.dataContext.id,
      canonicalTypeId: destTable.canonicalTypeId,
      userSelectionAction: {
        type: TpEnSelectionFilterTypes.MANUAL,
        description: `Transferred selection from ${sourceCanonicalTypeInfo.name}`,
        method: 'addFilter',
        stepIdentifier: step.stepIdentifier,
        selectedItemIds: matchingDestItemIds,
      },
    }),
  );

  onCompleted();
}

const dbActionStepTypeTransferSelection: TpDbActionStepTypeDefinition = {
  stepType: 'TransferSelection',
  execute,
  getJsonSchema,
};

export default dbActionStepTypeTransferSelection;
